import React from 'react';
import { Chart, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { SEV_COLORS } from './constants';
import { ProjectBucket } from './types';
import ChartCard from './ChartCard';
import { useChartTheme } from './useChartTheme';

Chart.register(Tooltip, Legend, BarElement, LinearScale, CategoryScale);

interface FindingDistributionProps {
  buckets: ProjectBucket[];
  /** Max projects to display; defaults to 10 */
  maxProjects?: number;
}

const SEVERITY_DATASETS = [
  { label: 'Critical', color: SEV_COLORS.Critical, rating: 1 },
  { label: 'High',     color: SEV_COLORS.High,     rating: 2 },
  { label: 'Medium',   color: SEV_COLORS.Medium,   rating: 3 },
  { label: 'Low',      color: SEV_COLORS.Low,       rating: 4 },
] as const;

// Visible viewport height for the widget; taller project lists scroll within it
// instead of compressing rows below BAR_THICKNESS, which is what caused bars to overlap.
const VIEWPORT_HEIGHT = 280;
const BAR_THICKNESS = 32;
const ROW_HEIGHT = BAR_THICKNESS + 8;

const FindingDistribution: React.FC<FindingDistributionProps> = ({
  buckets,
  maxProjects = 10,
}) => {
  const { textColor } = useChartTheme();

  const top = [...buckets]
    .sort((a, b) => b.findings.length - a.findings.length)
    .slice(0, maxProjects);

  const chartData = {
    labels: top.map(b =>
      b.projectName.length > 18 ? b.projectName.slice(0, 16) + '…' : b.projectName
    ),
    datasets: SEVERITY_DATASETS.map(({ label, color, rating }) => ({
      label,
      backgroundColor: color,
      data: top.map(b => b.findings.filter(f => f.rating === rating).length),
      borderRadius: 0,
      barThickness: BAR_THICKNESS,
    })),
  };

  const options = {
    indexAxis: 'y' as const,
    plugins: { legend: { display: false } },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: textColor, font: { size: 11 } } },
      y: { stacked: true, grid: { display: false }, ticks: { color: textColor, font: { size: 11 } } },
    },
    responsive: true,
    maintainAspectRatio: false,
  } as any;

  return (
    <ChartCard
      title="Finding distribution"
      sub="Findings per project, stacked by severity"
    >
      {top.length > 0 ? (
        <>
          <div className="chart-scroll-y" style={{ height: VIEWPORT_HEIGHT }}>
            <div style={{ height: Math.max(top.length * ROW_HEIGHT, VIEWPORT_HEIGHT) }}>
              <Bar data={chartData} options={options} />
            </div>
          </div>
          <div className="d-flex gap-4 mt-2 small flex-wrap">
            {SEVERITY_DATASETS.map(({ label, color }) => (
              <span key={label} className="d-flex align-items-center gap-1">
                <span style={{ width: 10, height: 10, background: color, display: 'inline-block' }} />
                {label}
              </span>
            ))}
          </div>
        </>
      ) : (
        <div className="text-muted text-center py-5 small">
          No findings match the current filters
        </div>
      )}
    </ChartCard>
  );
};

export default FindingDistribution;
